/**
 * Search State Management
 * Centralized state object and localStorage persistence
 */

import { STATE_STORAGE_KEY } from './constants.js';
import { normalizeBrands } from '../utils/brand-utils.js';

/**
 * @typedef {Object} SearchState
 * @property {string} zip - Location query
 * @property {boolean|null} live - Live camps filter
 * @property {string[]} sportSlug - Selected sports
 * @property {string[]} types - Fee code filters
 * @property {string[]} gender - Selected gender
 * @property {string[]} brand - Selected brands
 * @property {string} age - Age filter
 * @property {string} sort - Sort order
 * @property {string[]} tier - Hidden tier filter
 * @property {number} page - Current page
 * @property {number} hitsPerPage - Results per page
 * @property {number} displayedHits - Displayed results count
 * @property {Array} allFilteredHits - All results after filtering
 * @property {number} totalHits - Total results count
 * @property {Object} coordinates - Search center {lat, lng}
 * @property {string} beginDates - Start date filter
 * @property {string} endDates - End date filter
 * @property {Object} geocodeResult - Google Geocoding API result
 * @property {Object} facets - Facet counts from Algolia
 * @property {Object} dualSearchLocation - Location from dual search { city, state }
 * @property {string[]|null} implicitSportSlug - Sport from found camp (not in URL)
 */

export const state = {
  zip: '',
  live: null, // null = not set (show all), true = only live, false = only non-live
  sportSlug: [],
  types: [], // Used in URL and state, mapped to feeCodes for Algolia
  gender: [],
  brand: [],
  skills: [],
  brandExclude: [],              // brands to exclude (NOT filter) — only "All others" checked in brand page mode
  brandPageMode: false,          // true when arriving from a brand page via typeBrand URL param
  brandPageBrand: null,          // original brand name from the brand page (e.g. "Nike")
  brandPageBrandSelected: false, // visual checked state of the specific brand checkbox
  brandPageAllOthers: false,     // visual checked state of "All other brands" checkbox
  age: '',
  sort: 'distance',
  tier: null,
  page: 1,
  hitsPerPage: window.innerWidth < 768 ? 6 : 12,
  displayedHits: window.innerWidth < 768 ? 6 : 12,
  allFilteredHits: [],
  totalHits: 0,
  isInitialSearch: true, // True only for the first search; enables live auto-fallback
  coordinates: null, // {lat, lng} for geo search
  beginDates: '', // Date range start (YYYY-MM-DD)
  endDates: '', // Date range end (YYYY-MM-DD)
  geocodeResult: null,
  isStateSearch: false,
  searchStateCode: null,
  searchStateName: null,
  isExcludeUSSearch: false,
  excludeUSRegionLabel: null,
  regionFilter: null, // 'scotland'|'england'|'uk'|'london'|'canada'|null
  ukStateValue: null, // UK county/city name for Algolia
  ukCountryFilter: null, // Individual UK country name for filtering (e.g., "England", "Scotland")
  sortByDistanceNoRadius: false, // England/Scotland/UK: use coords for sorting, don't show/limit radius
  showDistance: true,
  embeddedRadius: null, // Radius in miles set by embedded mode (overrides default)
  embeddedCustomValue: null, // Custom value for embedded heading (e.g. "Overnight")
  embeddedSportTitle: null, // Sport title for embedded heading (e.g. "Basketball")
  embeddedPresetLocation: null, // Preset location string (e.g. "California" or "Los Angeles, California")
  showCorrectedLocationBanner: false,
  showUnrecognizedLocationBanner: false,
  unrecognizedLocationBannerContent: null,

  // Dual search state (camp title search)
  dualSearchLocation: null, // { city, state } from found camp location
  implicitSportSlug: null, // Sport from found camp (not in URL, used for filtering)
  foundCampObjectID: null, // ObjectID of camp found by title search (to exclude from location results)

  // Gender filter checkboxes state
  filterCoed: false,
  filterMaleOnly: false,
  filterFemaleOnly: false,

  facets: {
    sportSlug: {},
    feeCodes: {}, // Algolia uses feeCodes
    gender: {},
    brands: {}, // Algolia brands (array)
    skills: {}, // Algolia skills (array)
    live: {},
  },

  // Keep initial (unfiltered) facet keys so options don't disappear in UI
  allFacets: {
    feeCodes: {},
    brands: {},
    skills: {},
  },
};

// Flag to track if state has been initialized
export let stateInitialized = false;

/**
 * Mark state as initialized
 */
export function markStateInitialized() {
  stateInitialized = true;
}

/**
 * Save search state to localStorage
 */
export function saveToLocalStorage() {
  if (document.querySelector('[data-algolia-mode="embedded"]')) return; // Embedded mode: don't pollute localStorage
  try {
    const stateToSave = {
      zip: state.zip,
      live: state.live,
      sportSlug: state.sportSlug,
      types: state.types,
      skills: state.skills,
      gender: state.gender,
      brand: state.brand,
      age: state.age,
      sort: state.sort,
      tier: state.tier,
      beginDates: state.beginDates,
      endDates: state.endDates,
      filterCoed: state.filterCoed,
      filterMaleOnly: state.filterMaleOnly,
      filterFemaleOnly: state.filterFemaleOnly,
      timestamp: Date.now(),
    };
    localStorage.setItem(STATE_STORAGE_KEY, JSON.stringify(stateToSave));
    console.log('💾 State saved to localStorage:', stateToSave);

    // Note: userLocation in geolocation cache is updated via updateCachedLocationWithGeocode()
    // after successful geocoding in initFromURL(), not here
  } catch (error) {
    console.warn('Failed to save state to localStorage:', error);
  }
}

/**
 * Load search state from localStorage
 * @returns {Object|null} - Saved state or null
 */
export function loadFromLocalStorage() {
  try {
    const saved = localStorage.getItem(STATE_STORAGE_KEY);
    if (!saved) return null;

    const parsed = JSON.parse(saved);
    // Check if state is not too old (e.g., 30 days)
    const maxAge = 30 * 24 * 60 * 60 * 1000; // 30 days
    if (parsed.timestamp && Date.now() - parsed.timestamp > maxAge) {
      localStorage.removeItem(STATE_STORAGE_KEY);
      return null;
    }

    console.log('📂 State loaded from localStorage:', parsed);
    return parsed;
  } catch (error) {
    console.warn('Failed to load state from localStorage:', error);
    localStorage.removeItem(STATE_STORAGE_KEY);
    return null;
  }
}

// "Show only available camps" choice for the browser session. Set by an explicit
// live=1/0 in the URL, by the checkbox and by Clear All. A URL without live uses
// it before the CP default, so an unchecked box stays unchecked after reload or
// a new search, and live=1 from an ad link survives searches from other pages.
const LIVE_CHOICE_KEY = 'camps_search_live';

/**
 * @param {boolean} live
 */
export function saveLiveChoice(live) {
  try {
    sessionStorage.setItem(LIVE_CHOICE_KEY, live ? '1' : '0');
  } catch (e) {
    /* ignore storage errors */
  }
}

/**
 * @returns {true|null|undefined} - true/null (unchecked) or undefined when not set
 */
export function loadLiveChoice() {
  try {
    const value = sessionStorage.getItem(LIVE_CHOICE_KEY);
    if (value === '1') return true;
    if (value === '0') return null;
  } catch (e) {
    /* ignore storage errors */
  }
  return undefined;
}

/**
 * Update facets in state
 * @param {Object} facets
 */
export function updateFacets(facets) {
  if (facets) {
    // Don't update sportSlug facets - we'll hide this filter
    // Map feeCodes from Algolia to types for UI (monolith parity; decision-flow
    // steps pass raw Algolia facets where camp types live under `feeCodes`)
    state.facets.types = facets.feeCodes || facets.types || {};
    state.facets.feeCodes = facets.feeCodes || {};
    state.facets.gender = facets.gender || {};
    state.facets.brands = facets.brands || {};
    state.facets.skills = facets.skills || {};
    state.facets.live = facets.live || {};

    // Normalize brands after facets are updated
    state.brand = normalizeBrands(state.brand);
  }
}

/**
 * Merge new facets with existing ones
 * @param {Object} newFacets
 */
export function mergeFacets(newFacets) {
  if (newFacets) {
    state.facets = {
      ...state.facets,
      ...newFacets,
    };
  }
}

/**
 * Update allFacets (initial unfiltered facets)
 * @param {Object} allFacets
 */
export function updateAllFacets(allFacets) {
  if (allFacets) {
    state.allFacets = {
      ...state.allFacets,
      ...allFacets,
    };
  }
}

console.log('📦 State module loaded');
